import {useCallback, useEffect, useState} from 'react';
import {fetchNetwork} from './api/aptos.mjs';
import Globe from './components/Globe';
import Breakdown from './components/Breakdown';
import ValidatorTable from './components/ValidatorTable';
import {compact, duration, integer, percent} from './lib/format';
import './css/App.css';

const REFRESH_MS = 60_000;

function Kpi({label, value, hint}) {
    return (
        <div className="card kpi">
            <span className="kpi-label">{label}</span>
            <span className="kpi-value">{value}</span>
            {hint && <span className="kpi-hint">{hint}</span>}
        </div>
    );
}

function useNow(interval = 30_000) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), interval);
        return () => clearInterval(id);
    }, [interval]);
    return now;
}

export default function App() {
    const [network, setNetwork] = useState(null);
    const [error, setError] = useState(null);
    const [focus, setFocus] = useState(null);
    const now = useNow();

    const load = useCallback(() => {
        fetchNetwork()
            .then(data => { setNetwork(data); setError(null); })
            .catch(err => setError(err.message));
    }, []);

    useEffect(() => {
        load();
        // Only poll while the tab is visible, to stay well inside the public API's rate limit.
        const id = setInterval(() => { if (!document.hidden) load(); }, REFRESH_MS);
        const onVisible = () => { if (!document.hidden) load(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [load]);

    const s = network?.stats;
    const chain = network?.chain;
    const snapshotDate = network?.source.generatedAt && new Date(network.source.generatedAt).toLocaleDateString(undefined, {month: 'short', day: 'numeric'});

    return (
        <div className="app">
            <header className="topbar">
                <div className="brand">
                    <svg className="logo" viewBox="0 0 32 32" aria-hidden="true">
                        <circle cx="16" cy="16" r="15" fill="none" stroke="currentColor" strokeWidth="2" />
                        <path d="M5 12h22M3.5 17h25M5 22h22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <div>
                        <h1>Aptos Validator Network</h1>
                        <p className="muted">Live view of the mainnet validator set</p>
                    </div>
                </div>
                <div className="status">
                    {error && network && <span className="pill warn">Refresh failed — showing last data</span>}
                    {chain && (
                        <>
                            <span className="pill live"><i />Mainnet</span>
                            <span className="pill">Epoch {integer(chain.epoch)} · next in {duration(chain.epochEnd - now)}</span>
                            <span className="pill mono">Block {integer(chain.blockHeight)}</span>
                        </>
                    )}
                </div>
            </header>

            {!network && !error && (
                <div className="loading">
                    <div className="spinner" />
                    <span>Reading validator set from Aptos mainnet…</span>
                </div>
            )}

            {!network && error && (
                <div className="card error-card">
                    <h2>Couldn’t reach the Aptos API</h2>
                    <p className="muted mono">{error}</p>
                    <button className="button" onClick={load}>Try again</button>
                </div>
            )}

            {network && (
                <>
                    <section className="kpis">
                        <Kpi label="Active validators" value={s.count} hint={`${s.countries} countries`} />
                        <Kpi label="Total staked" value={`${compact(s.totalStake)} APT`} hint="Voting power this epoch" />
                        <Kpi label="Staking APR" value={percent(s.apr, 2)} hint="Protocol reward rate" />
                        <Kpi label="Proposal success" value={percent(s.proposalSuccess, 2)} hint={`${integer(s.totalProposals)} proposals this epoch`} />
                        <Kpi label="Governance turnout" value={percent(s.governanceParticipation, 0)} hint={`Last ${s.governanceWindow} proposals${snapshotDate ? ` · as of ${snapshotDate}` : ''}`} />
                        <Kpi label="Nakamoto coefficient" value={s.nakamoto} hint="Validators to control ⅓ stake" />
                    </section>

                    <section className="main-grid">
                        <div className="card globe-card">
                            <div className="card-head">
                                <div>
                                    <h2>Validator locations</h2>
                                    <p className="muted">{s.located} of {s.count} located · size = voting power · links show a sample of the full consensus mesh</p>
                                </div>
                                <div className="legend">
                                    <span><i style={{background: 'var(--good)'}} />Healthy</span>
                                    <span><i style={{background: 'var(--warn)'}} />Degraded</span>
                                    <span><i style={{background: 'var(--bad)'}} />Failing</span>
                                </div>
                            </div>
                            <Globe validators={network.validators} focus={focus} />
                        </div>
                        <aside className="side">
                            <Breakdown title="By continent" rows={network.breakdowns.continents} total={s.count} />
                            <Breakdown title="By country" rows={network.breakdowns.countries} total={s.count} />
                            <Breakdown title="Hosting providers" rows={network.breakdowns.providers} total={s.count} />
                        </aside>
                    </section>

                    <ValidatorTable
                        validators={network.validators}
                        governanceWindow={s.governanceWindow}
                        selected={focus?.address}
                        onSelect={v => { setFocus(v); window.scrollTo({top: 0, behavior: 'smooth'}); }}
                    />
                </>
            )}

            <footer className="footer">
                <span className="muted">
                    Live data: Aptos fullnode API{network?.source.slowData === 'live' && ' & indexer'}{network && ` · updated ${new Date(network.fetchedAt).toLocaleTimeString()}`}
                    {snapshotDate && ` · operators, governance & locations as of ${snapshotDate}`}
                </span>
                <a href="https://encapsulate.xyz/" target="_blank" rel="noopener noreferrer">Made by Encapsulate</a>
            </footer>
        </div>
    );
}
